import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError, download } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { dateTime } from '../../lib/format';
import { Field, Loading, Modal, TextInput, useToast } from '../ui';

/**
 * Taking the whole account's data out, and closing the account (DPDP Phase
 * 11). The 30-day grace period is SARTHI's own policy (Terms §17), not a
 * DPDP requirement. The owner's: both need property:manage, and closing also needs a
 * code emailed to the owner's registered address.
 *
 * The export is generated when asked for and downloaded straight away -
 * SARTHI keeps no copy. Closing starts a 30-day grace period (Terms §17)
 * in which the account can still export, and can change its mind; after it,
 * SARTHI erases the account's personal data and keeps only the records
 * the law requires.
 */

interface Closure {
  id: string;
  status: 'REQUESTED' | 'GRACE_PERIOD' | 'PROCESSING' | 'COMPLETED' | 'CANCELLED' | 'FAILED';
  requestedAt: string;
  graceEndsAt: string;
  lastExportAt: string | null;
}

export function ExportAccountButton({ className = 'btn' }: { className?: string }) {
  const { fail } = useToast();
  const [busy, setBusy] = useState(false);
  return (
    <button
      className={className}
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await download('/api/account/export', `sarthi-account-export-${new Date().toISOString().slice(0, 10)}.json`);
        } catch (error) {
          fail(error);
        } finally {
          setBusy(false);
        }
      }}
    >
      {busy ? 'Preparing…' : 'Download all account data'}
    </button>
  );
}

export function AccountExitPanel() {
  const { push, fail } = useToast();
  const { refreshSubscription } = useAuth();
  const queryClient = useQueryClient();
  const [closing, setClosing] = useState(false);
  const closure = useQuery({
    queryKey: ['account-offboarding'],
    queryFn: () => api.get<Closure | null>('/api/account/offboarding'),
  });
  const cancel = useMutation({
    mutationFn: () => api.post('/api/account/offboarding/cancel'),
    onSuccess: async () => {
      push('Closure cancelled - the account is open again', 'ok');
      await queryClient.invalidateQueries({ queryKey: ['account-offboarding'] });
      await refreshSubscription();
    },
    onError: fail,
  });

  if (closure.isLoading) return <Loading />;
  const current = closure.data && closure.data.status !== 'CANCELLED' ? closure.data : null;

  return (
    <div className="stack">
      <div className="card">
        <div className="card-head">
          <h3>Your account's data</h3>
        </div>
        <div className="card-body col gap-14">
          <p className="small">
            One JSON file with everything this account holds - properties, rooms, guests, stays, orders, invoices,
            staff, payroll, stock, consent records and the audit trail. Passwords, sign-in secrets and payment-gateway
            keys are left out; guests' ID numbers appear as their last four digits. It is made when you ask and not kept
            by SARTHI.
          </p>
          <div>
            <ExportAccountButton className="btn primary" />
          </div>
          {current?.lastExportAt ? (
            <div className="xs muted">Last downloaded {dateTime(current.lastExportAt)}</div>
          ) : null}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>Close this account</h3>
        </div>
        <div className="card-body col gap-14">
          {current ? (
            <>
              <p className="small">
                Closure requested {dateTime(current.requestedAt)}.{' '}
                {current.status === 'GRACE_PERIOD' || current.status === 'REQUESTED'
                  ? `This is SARTHI's account closure grace period: you can download your data or change your mind until ${dateTime(current.graceEndsAt)}. After that the account's personal data is erased, except what records the law requires to be kept still contain.`
                  : `Status: ${current.status.toLowerCase()}.`}
              </p>
              {current.status === 'GRACE_PERIOD' || current.status === 'REQUESTED' ? (
                <div>
                  <button className="btn" disabled={cancel.isPending} onClick={() => cancel.mutate()}>
                    Keep the account open
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            <>
              <p className="small">
                Closing stops everyday use at once. A 30-day grace period follows (SARTHI's policy) in which you can
                still download your data and change your mind. After it, guests', staff and customers' personal details
                are erased and sign-ins are closed. Records the law may require to be kept - tax invoices, payroll
                figures and similar - are retained, with the personal data they contain, only for as long as that
                requires. Encrypted backups expire on their own schedule. Download your data first - you will need your
                invoices for years.
              </p>
              <div>
                <button className="btn danger" onClick={() => setClosing(true)}>
                  Close this account…
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      {closing ? <CloseAccountModal onClose={() => setClosing(false)} /> : null}
    </div>
  );
}

interface ClosureCode {
  sent: boolean;
  /** The owner's registered address, masked. */
  to: string;
  expiresAt: string | null;
}

/**
 * Closing takes a code emailed to the owner's registered address: a signed-in
 * session alone - a shared till left open, a stolen password - cannot close
 * an account and start the clock on its data.
 */
function CloseAccountModal({ onClose }: { onClose: () => void }) {
  const { push, fail } = useToast();
  const { refreshSubscription } = useAuth();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState<ClosureCode | null>(null);
  const [error, setError] = useState<string | null>(null);

  const send = useMutation({
    mutationFn: () => api.post<ClosureCode>('/api/account/offboarding/code'),
    onSuccess: (result) => {
      setChallenge(result);
      setCode('');
      setError(
        result.sent
          ? null
          : 'We could not email the code just now. Try again in a moment, or contact SARTHI support.',
      );
    },
    onError: fail,
  });

  const close = useMutation({
    mutationFn: () => api.post('/api/account/offboarding', { reason, code }),
    onSuccess: async () => {
      push('Closure requested', 'ok');
      await queryClient.invalidateQueries({ queryKey: ['account-offboarding'] });
      await refreshSubscription();
      onClose();
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Could not close the account.'),
  });

  const codeSent = challenge?.sent === true;

  return (
    <Modal
      title="Close this account?"
      size="narrow"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          {codeSent ? (
            <button
              className="btn danger"
              disabled={close.isPending || !/^\d{6}$/.test(code)}
              onClick={() => {
                setError(null);
                close.mutate();
              }}
            >
              Close the account
            </button>
          ) : (
            <button className="btn primary" disabled={send.isPending} onClick={() => send.mutate()}>
              {send.isPending ? 'Sending…' : 'Email me a code'}
            </button>
          )}
        </>
      }
    >
      <div className="col gap-14">
        <p className="small">
          Everyday use stops now. During the 30-day grace period you can download your data or change your mind.
        </p>
        <Field label="Why are you leaving? (optional)">
          <textarea className="input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        {error ? <div className="form-error">{error}</div> : null}
        {codeSent ? (
          <>
            <p className="small">
              We emailed a 6-digit code to <b>{challenge?.to}</b>
              {challenge?.expiresAt ? `. It works until ${new Date(challenge.expiresAt).toLocaleTimeString()}` : ''}.
            </p>
            <TextInput
              label="Code from the email"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
            <div>
              <button className="linklike small" disabled={send.isPending} onClick={() => send.mutate()}>
                Send another code
              </button>
            </div>
          </>
        ) : (
          <p className="small muted">
            To confirm, we will email a code to the owner's registered address.
          </p>
        )}
      </div>
    </Modal>
  );
}
